import { useState, useEffect } from 'react';
import { usePrevious } from '@uidotdev/usehooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAccount, useWaitForTransactionReceipt } from 'wagmi';
import { toast } from 'react-toastify';
import { useBlockExplorerURL } from '@/utils/url/blockExplorer';
import { garnetHolesky } from '@/chains/garnetHolesky';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { formatEther } from 'viem';
import { Button } from '@/ui/PrimaryButton';
import { Disclaimer } from '../Disclaimer';

export function DeprecatedETHClaim() {
  const { address } = useAccount();
  const { openConnectModal } = useConnectModal();
  const [isWaitingTx, setIsWaitingTx] = useState(false);

  const {
    data: claimData,
    isFetched,
    isLoading: isLoadingBalance,
    refetch,
  } = useQuery({
    queryKey: ['claim-eth', address],
    queryFn: async () => {
      const res = await fetch(`/api/claim-eth?to=${address}`);
      return res.json();
    },
    enabled: Boolean(address),
  });

  const {
    data,
    mutate: claim,
    isPending: isPendingClaim,
  } = useMutation({
    mutationFn: async () => {
      setIsWaitingTx(true);
      const res = await fetch('/api/claim-eth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to: address }),
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error);
        setIsWaitingTx(false);
      } else {
        toast.info('Transaction submitted. Waiting for confirmations...');
      }
      return data;
    },
  });

  const isPending = isPendingClaim || isWaitingTx || isLoadingBalance;
  const isDisabled =
    Boolean(address) && (isPending || Boolean(claimData?.error) || claimData?.claimed);

  const txUrl = useBlockExplorerURL(garnetHolesky.id, data?.hash);
  const waitForReceipt = useWaitForTransactionReceipt({
    hash: data?.hash,
    chainId: garnetHolesky.id,
  });
  const prevTxStatus = usePrevious(waitForReceipt.data?.status);

  useEffect(() => {
    if (!prevTxStatus && waitForReceipt.data?.status === 'success') {
      toast.success('Transaction confirmed');
    } else if (!prevTxStatus && waitForReceipt.data?.status === 'reverted') {
      toast.error('Transaction failed');
    }

    refetch();
    setIsWaitingTx(false);
  }, [prevTxStatus, waitForReceipt.data?.status, refetch]);

  const onSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    if (isDisabled) {
      return;
    }

    if (!address) {
      if (!openConnectModal) {
        throw new Error('Missing openConnectModal');
      }
      return openConnectModal();
    }
    await claim();
  };

  return (
    <>
      {(isFetched || !address) && (
        <Disclaimer className="mb-8">
          <>
            {isFetched && claimData?.balance && claimData?.claimed && (
              <p>Eligible Garnet ETH has already been claimed.</p>
            )}
            {isFetched && claimData?.balance && !claimData?.claimed && (
              <p>You are eligible to claim {formatEther(claimData.balance)} Garnet ETH.</p>
            )}
            {isFetched && Boolean(address) && claimData?.error && <p>{claimData.error}</p>}
            {!address && (
              <p>
                Connect wallet to check your eligibility to claim Garnet ETH from the deprecated
                testnet.
              </p>
            )}
          </>
        </Disclaimer>
      )}

      <form onSubmit={onSubmit}>
        <div className="inline-block text-center">
          <Button
            type="submit"
            className="px-6"
            label={!address ? 'Connect wallet' : 'Claim Garnet ETH'}
            pending={isPending}
            disabled={isDisabled}
          />

          {address && data?.hash && (
            <a href={txUrl} target="_blank" rel="noreferrer">
              <p className="mt-3 text-white/80">View on block explorer</p>
            </a>
          )}
        </div>
      </form>
    </>
  );
}
